import {computed, extendObservable, observable} from 'mobx';
import {Context, EMPTY_CONTEXT, Options, Schema} from './schema';

export type RecordType<T> = Record<keyof T, any>;
export type FieldsSchemas<T> = {[F in keyof T]: Schema<T[F]>};

export interface RecordOptions<T extends RecordType<T>>
  extends Options<T | null> {}

export class RecordSchema<
  T extends RecordType<S> = RecordType<S>,
  S extends FieldsSchemas<T> = FieldsSchemas<T>
> extends Schema<T | null, RecordOptions<T>> {
  protected _fieldsSchemas: S;
  protected _fields: Array<keyof S>;

  constructor(
    // Force type parameter `T` to be inferred from the fields' schema
    fieldsSchemas: S & FieldsSchemas<T>,
    options: RecordOptions<T> = {},
  ) {
    super(options);
    this._fieldsSchemas = fieldsSchemas;
    this._fields = Object.keys(fieldsSchemas);
  }

  public toJSON(): object {
    return {
      type: 'RecordSchema',
      ...super.toJSON(),
      fieldsSchemas: this._fieldsSchemas,
    };
  }

  public get fields(): Array<keyof S> {
    return this._fields;
  }

  public get fieldsSchemas(): S {
    return this._fieldsSchemas;
  }

  public fieldSchema(field: string): Schema {
    return this._fieldsSchemas[field];
  }

  // When defined, merge the schema's initial value with its fields'
  public initialValue(context: Context = EMPTY_CONTEXT): T | null {
    const schemaInitialValue = super.initialValue(context);
    return schemaInitialValue === null
      ? null
      : this._merge(
          this.fieldsInitialValues(context),
          // Filter out fields not in schema
          this._fields.reduce((obj: any, field) => {
            if (field in schemaInitialValue) {
              obj[field] = schemaInitialValue[field];
            }
            return obj;
          }, {}),
        );
  }

  // Initial value for all fields of the record
  public fieldsInitialValues(context: Context = EMPTY_CONTEXT): T {
    // Although the order of the fields should not be relied upon, a field's
    // initial value may access the initial values of fields defined before it
    return this._fields.reduce((obj: any, field) => {
      obj[field] = this.fieldInitialValue(obj, field, context);
      return obj;
    }, {}) as T;
  }

  // Initial value for field `field` of `value`
  public fieldInitialValue(
    value: T,
    field: string,
    context: Context = EMPTY_CONTEXT,
  ) {
    return this._fieldsSchemas[field].initialValue(
      this._buildChildContext(value, field, context),
    );
  }

  public valueToMobx(
    value: T | null,
    context: Context = EMPTY_CONTEXT,
  ): T | null {
    const record = this._nullifyValue(value);
    if (record === null) {
      return null;
    }
    // Return a MobX object with derived properties for each field that has
    // computed properties
    const mobxRecord: any = observable.shallowObject({});
    extendObservable(
      mobxRecord,
      this._fields.reduce((obj: any, field) => {
        const fieldSchema = this._fieldsSchemas[field];
        const childContext = this._buildChildContext(
          mobxRecord,
          field,
          context,
        );
        obj[field] = fieldSchema.isComputed
          ? computed(() =>
              fieldSchema.valueToMobx(
                fieldSchema.computedValue(childContext),
                childContext,
              ),
            )
          : fieldSchema.valueToMobx(record[field], childContext);
        return obj;
      }, {}),
    );
    return mobxRecord;
  }

  public fieldToMobx(
    value: T,
    field: string,
    fieldValue: any,
    context: Context = EMPTY_CONTEXT,
  ): any {
    return this._fieldsSchemas[field].valueToMobx(
      fieldValue,
      this._buildChildContext(value, field, context),
    );
  }

  public valueToJS(
    value: T | null,
    context: Context = EMPTY_CONTEXT,
  ): T | null {
    const record = this.isComputed
      ? this.computedValue(context)
      : this._nullifyValue(value);
    if (record === null) {
      return null;
    }
    // Return a lazy record with "getters" for each computed field
    const lazyRecord: any = {};
    for (const field of this._fields) {
      const fieldSchema = this._fieldsSchemas[field];
      if (fieldSchema.isComputed) {
        Object.defineProperty(lazyRecord, field, {
          enumerable: true,
          configurable: true,
          get: () =>
            fieldSchema.valueToJS(
              null,
              this._buildChildContext(lazyRecord, field, context),
            ),
        });
      } else {
        lazyRecord[field] = fieldSchema.valueToJS(
          record[field],
          this._buildChildContext(lazyRecord, field, context),
        );
      }
    }
    return lazyRecord;
  }

  // When not in strict mode, a record value is of a valid type if it contains a
  // subset of the schema's fields and all of them are of valid type; otherwise
  // the record has to contain all of the schema's fields
  public typeIsValid(value: any, strict: boolean = false): value is T | null {
    if (value === null) {
      return true;
    }
    if (typeof value !== 'object' || Array.isArray(value)) {
      return false;
    }
    const valueFields = Object.keys(value);
    return (
      (strict
        ? valueFields.length === this._fields.length
        : valueFields.length <= this._fields.length) &&
      valueFields.every(
        field =>
          field in this._fieldsSchemas &&
          this._fieldsSchemas[field].typeIsValid(value[field], strict),
      )
    );
  }

  public valueIsValid(
    value: T | null,
    context: Context = EMPTY_CONTEXT,
  ): boolean {
    return (
      this.requiredIsValid(value, context) &&
      this.fieldsAreValid(value, context) &&
      this.customFunctionIsValid(value, context)
    );
  }

  public fieldIsValid(
    value: T,
    field: string,
    context: Context = EMPTY_CONTEXT,
  ): boolean {
    return (
      !(field in value) ||
      this._fieldsSchemas[field].valueIsValid(
        value[field],
        this._buildChildContext(value, field, context),
      )
    );
  }

  public fieldsAreValid(
    value: T | null,
    context: Context = EMPTY_CONTEXT,
  ): boolean {
    return (
      value === null ||
      this._fields.every(field => this.fieldIsValid(value, field, context))
    );
  }
}
